// What the Neo panel says when it is opened to edit something (TOM-356,
// boards-v3.md "Edit with Neo: the panel and the phone sheet"): a board, or an
// item on a board. (The owner's quick readings left the web in TOM-526.) Pure;
// NeoChat draws it.

/**
 * What a chat request names as on screen (cloud ADR 0302 §1, TOM-348).
 * `target` is absent for a board the owner is only looking at, and
 * `'board_edit'` from the board's own editing surfaces (TOM-543, cloud ADR 0341
 * §4), which load the board editor into Neo's turn. `'explain'` is "Ask Neo
 * about this" on one panel (TOM-706, cloud ADR 0406): the server reads what the
 * panel shows from the stored board, and never loads the board editor.
 */
export interface NeoRequestContext {
  board?: string;
  item?: string;
  /** The panels selected on the board in edit mode, when more than one (TOM-670). */
  items?: string[];
  target?: 'board' | 'board_edit' | 'explain';
  /** With `'explain'` only: the span the owner is asking about (TOM-706). */
  range?: NeoExplainRange;
}

/**
 * A span of time as two UTC ISO instants (TOM-706). The server takes `from`
 * before `to`, at most 31 days apart, ending no later than a minute from now;
 * any other drops the context, and the turn runs without it.
 */
export interface NeoExplainRange {
  from: string;
  to: string;
}

/**
 * An explain context for one panel on a board (TOM-706, cloud ADR 0406): the
 * board's slug, the item's id and target `'explain'`. "Ask Neo" on a panel
 * sent it until TOM-958, which asks the owner what to change instead
 * (askNeoAboutPanelDetail); the cloud still takes it.
 * `range` is the span the owner is asking about when it is not the panel's
 * own: a span picked on its chart. Leave it out and the server reads the
 * panel's own window as of the request, or none for a panel that shows the
 * latest readings. Dates become UTC ISO instants; a range with a date that is
 * not one is left out, so the panel's own window is read instead.
 */
export function explainPanelContext(
  board: string,
  item: string,
  range?: { from: Date | string; to: Date | string }
): NeoRequestContext {
  const from = range === undefined ? NaN : new Date(range.from).getTime();
  const to = range === undefined ? NaN : new Date(range.to).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to)) return { board, item, target: 'explain' };
  return {
    board,
    item,
    target: 'explain',
    range: { from: new Date(from).toISOString(), to: new Date(to).toISOString() },
  };
}

/**
 * The context the board's editing surfaces send: the Edit chat its Edit opens,
 * Edit with Neo on the board or one of its items, and Start over with Neo with
 * the empty mode board's Ask Neo to set it up (TOM-543). The server reads it as
 * a board on screen (TOM-539) and, the board editor being live, loads its rules
 * and tools into the turn. The panels selected in edit mode (TOM-670) are its
 * scope: one is `item`, as an item's Edit with Neo always sent; several are
 * `items`.
 */
export function boardEditContext(board: string, items: readonly string[] = []): NeoRequestContext {
  if (items.length === 0) return { board, target: 'board_edit' };
  if (items.length === 1) return { board, item: items[0], target: 'board_edit' };
  return { board, items: [...items], target: 'board_edit' };
}

/**
 * What Neo asks after "Ask Neo" on a panel (Mike, 2026-10-31, TOM-958; the
 * iPad's TOM-954): "What would you like to change about the Wind panel?".
 */
export function askNeoPanelQuestion(title: string): string {
  const name = title.trim();
  return name === ''
    ? 'What would you like to change about this panel?'
    : `What would you like to change about the ${name} panel?`;
}

/**
 * What "Ask Neo" on a panel hands the chat through `nm:open-neo` (TOM-958):
 * open the main chat, where Neo asks what to change about the panel. Nothing
 * is sent. The owner's reply names the board and the panel as its context,
 * with no target: the board on screen with that item selected (cloud ADR 0302
 * §1), so "this" is the panel, and Neo changes it or answers about it. It used
 * to send "Explain “<panel>”" at once with target 'explain' (TOM-713). On a
 * board page wider than 1024 px the board's floating Edit chat takes it, as
 * the main chat's thread and not as a board edit (TOM-1049,
 * boardEditChatTakes). Everywhere else the main chat takes it: home's, and on
 * a board page the slide-in chat or the phone's sparkle chat.
 */
export function askNeoAboutPanelDetail(
  board: string,
  item: { id: string; title: string }
): { askAboutPanel: { title: string }; context: NeoRequestContext } {
  return { askAboutPanel: { title: item.title }, context: { board, item: item.id } };
}

/** True for a context the board's Edit chat takes: a board edit, never an explain. */
export function isBoardEditContext(context: NeoRequestContext | null | undefined): boolean {
  return context?.target === 'board_edit';
}

/**
 * Wider than 1024 px the board's Edit chat floats over the page (TOM-415) and
 * drags and resizes (TOM-527). A panel's Ask Neo opens it there too (TOM-1049).
 */
export const NEO_FLOAT_QUERY = '(min-width: 1025px)';

/** What decides which chat takes a `nm:open-neo` request on a board page. */
export interface NeoOpenRequest {
  context?: NeoRequestContext;
  askAboutPanel?: { title: string };
}

/**
 * True when the board's Edit chat takes a `nm:open-neo` request on a board
 * page, and the board's main chat (the slide-in or the phone's sparkle chat)
 * steps aside for it. A board edit goes to the Edit chat at every width
 * (TOM-547). A panel's Ask Neo goes to it only where it floats over the page,
 * `floating` (NEO_FLOAT_QUERY): the Edit chat then shows the main chat's
 * thread, with Neo's question about the panel, and the reply names the panel
 * with no target, never `'board_edit'` (TOM-1049, cloud ADR 0406 §4). Every
 * other request goes to the main chat. The Edit chat and the slide-in both
 * call this, so they agree.
 */
export function boardEditChatTakes(
  request: NeoOpenRequest | null | undefined,
  floating: boolean
): boolean {
  if (isBoardEditContext(request?.context)) return true;
  return floating && request?.askAboutPanel !== undefined && request.context !== undefined;
}

/** A panel selected on the board in edit mode, as the Edit chat names it (TOM-670). */
export interface NeoSelectedPanel {
  id: string;
  title: string;
}

/**
 * The board → Edit chat events of edit mode (TOM-670). The board tells the
 * chat its selection, and the chat tells the board when an owner unselects a
 * panel from its chip, or when it closes (`{ floating }`: whether it floated
 * beside the board, where closing it ends edit mode, or was a phone's sheet).
 */
export const NEO_EDIT_SCOPE_EVENT = 'nm:neo-edit-scope';
export const NEO_EDIT_UNSELECT_EVENT = 'nm:neo-edit-unselect';
export const NEO_EDIT_CHAT_CLOSED_EVENT = 'nm:neo-edit-chat-closed';

/** The selection the board hands the open Edit chat: its context, and the panels' titles. */
export interface NeoEditScopeDetail {
  context: NeoRequestContext;
  boardTitle: string;
  selection: NeoSelectedPanel[];
}

/**
 * The context the desktop Neo page sends while a board is open beside the chat
 * (TOM-357): the board on screen, never `'board_edit'`. Those are ordinary chat
 * turns, so they must not load the board editor (cloud ADR 0341 §4).
 */
export function boardBesideChatContext(board: string): NeoRequestContext {
  return { board };
}

/** What the panel was opened to edit, with the titles its copy names. */
export interface NeoEditTarget {
  context: NeoRequestContext;
  /** The board's title on screen; null when the opener didn't say. */
  boardTitle: string | null;
  /** The item's title, when the panel was opened from an item. */
  itemTitle: string | null;
  /**
   * The panels selected on the board in edit mode (TOM-670), which the chat
   * names as chips; absent or empty when none is.
   */
  selection?: readonly NeoSelectedPanel[];
}

/**
 * Several panels named in a line: "Depth and Wind", or "these 3 panels". The
 * field says "these 2 panels" even for two, so it fits on its one line; the
 * chips above it name them.
 */
function panelsSubject(selection: readonly NeoSelectedPanel[], short: boolean): string {
  if (selection.length === 2 && !short) {
    return `${selection[0]!.title} and ${selection[1]!.title}`;
  }
  return `these ${selection.length} panels`;
}

export interface NeoPanelCopy {
  /** The chip under the title: what a request will change. */
  chip: string;
  /** The field's placeholder. */
  field: string;
  /** The line under the field: who a change reaches, or that the boat is offline. */
  footer: string;
  /** The phone sheet's title and scope line. */
  sheetTitle: string;
  sheetScope: string;
  /** Beside the typing dots while the turn runs. */
  loading: string;
  /** When the turn failed. */
  error: string;
}

export function neoPanelCopy(
  target: NeoEditTarget,
  boatName: string | null,
  boatOffline: boolean
): NeoPanelCopy {
  const boat = boatName ?? 'the boat';
  const board = target.boardTitle ?? 'this board';
  const item = target.context.item !== undefined ? target.itemTitle : null;
  const selection = target.selection ?? [];
  const several = target.context.items !== undefined && selection.length > 1;
  const subject = several ? panelsSubject(selection, false) : (item ?? board);
  return {
    chip: several
      ? `${selection.length} panels · ${board}`
      : item !== null
        ? `${item} · ${board}`
        : `${board} · Shared board`,
    field: `Ask Neo to change ${several ? panelsSubject(selection, true) : subject}`,
    footer: boatOffline
      ? `${boat} is offline. Neo can still change boards; readings resume when the boat is back.`
      : `Board changes are shared with ${boat}’s owners.`,
    sheetTitle: `Edit ${subject}`,
    sheetScope: `Applies to ${board} for all ${boat} owners.`,
    loading: `Neo is changing ${subject}…`,
    error: `I couldn’t change ${board}. Nothing on it changed.`,
  };
}
